// src/diagnostics/nxrRules.js
// Regole della diagnostica dei tracker GPS (pagina Diagnostica dispositivo).
// La pagina NON parla con il firmware: legge i log che la scheda stampa sulla
// seriale USB e li confronta con queste regole. Quando cambia un firmware o
// arriva un modello nuovo si aggiorna SOLO questo file.
//
// Fonti: firmware NXR01B v0.4.x (brief) e NXR01C v0.6.0
// (racesense-ota/firmware/NXR01C_firmware.ino). Serve DEBUG_ENABLED 1: molte
// righe d'errore escono solo con il debug acceso.
//
// Come si leggono le regole di un controllo:
//   ok / fail / warn  elenchi di { re, msg?, detail? }: re cerca in QUALUNQUE
//                     punto della riga (alcune righe iniziano con "....." o con
//                     la risposta AT precedente: niente ^ all'inizio).
//   Vale l'ULTIMO esito che compare nel log: il firmware ritenta modem e rete,
//   e un errore al primo tentativo seguito da un OK è un OK.
//   steps             (rete) OK solo quando tutti i passi compaiono, nell'ordine,
//                     dopo l'ultimo errore.
//   failAs            'fail' (guasto, default) oppure 'warn' (da verificare).
//   dependsOn         se quel controllo non è OK, questo è "non verificabile".
//   onTimeout         esito se a fine test non è arrivato niente (default:
//                     guasto "nessuna risposta").
//   detail(m)         testo tecnico breve ricavato dalla riga (valori letti).

const n = (s) => Number(String(s).replace(',', '.'));
const fmt = (v, d = 1) => (Number.isFinite(v) ? v.toLocaleString('it-IT', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—');

export const NXR_RULES = {
  // Seriale USB: 115200 8N1, righe che finiscono con \r\n, testo UTF-8.
  serial: { baudRate: 115200, dataBits: 8, stopBits: 1, parity: 'none', flowControl: 'none', bufferSize: 65536 },

  timing: {
    resetPulseMs: 100,     // EN basso per 100 ms (RTS), poi avvio normale (DTR alto = GPIO0 libero)
    bannerWaitMs: 5000,    // dopo il reset: senza banner si chiede di premere reset
    noDataMs: 60000,       // nessun byte dalla scheda per così tanto: non comunica
    totalMs: 150000,       // durata massima, dall'ultimo avvio visto (modem + SIM + rete: fino a ~120 s)
    hardLimitMs: 300000,   // tetto assoluto del test
    reconnectMs: 8000,     // la porta può sparire e ricomparire durante il reset
  },

  // Tensione batteria (V)
  battery: { okMin: 3.4, okMax: 4.3, plausibleMin: 3.0, plausibleMax: 4.3 },
  // Segnale cellulare sotto questa soglia: da verificare
  signalWarnBelow: 20,

  // Modelli conosciuti: il profilo vale per IMU, lettore batteria e bus I2C.
  // expectedFirmware: versioni per cui sono scritte le regole (avviso se diversa).
  profiles: ['NXR01B', 'NXR01C'],

  common: {
    // ── avvio e identità ──
    bannerTop: /╔═/,
    banner: /GPS Tracker/,
    model: /GPS Tracker.*-\s*(NXR[0-9A-Z]+)/,
    version: /║\s*v(\d+\.\d+(?:\.\d+)?[\w.+-]*)\s/,
    device: /Device:\s*(\S+)/,
    server: /GPS UDP:\s*(\S+)/,
    config: /Config:\s*(DEBUG=\d.*)$/,
    debugOff: /Config:\s*DEBUG=0/,
    iccid: /\[SIM\] ICCID:\s*(\d{6,})/,
    fullTracking: /══ MODE: FULL TRACKING/,
    trackingSummary: /\|\s*v[\w.+-]+\s*\|\s*BAT\s+(\d+)%\s*\|\s*SIG\s+(\d+)%/,
    loop: /\[LOOP\]/,
    // la scheda torna a dormire: il test si chiude con quello che ha raccolto
    sleep: /\[SLEEP\] (?:Still \d+s → entering deep sleep|Deep sleep\.\.\.)/,
    // righe che dimostrano che la scheda sta parlando (anche senza banner)
    alive: /\[[A-Z]{2,}\]|Device:|GPS Tracker|ESP-ROM:|rst:0x/,
    // bootloader in modalità download: il reset ha tenuto basso GPIO0
    downloadMode: /waiting for download|DOWNLOAD\(USB\/UART0\)/,

    // ── avanzamento (testo per il cliente, in ordine) ──
    progress: [
      { re: /GPS Tracker/, pct: 8, label: 'Avvio della scheda…' },
      { re: /\[I2C\] Scan bus/, pct: 14, label: 'Controllo dei sensori interni…' },
      { re: /\[BAT\]/, pct: 20, label: 'Lettura della batteria…' },
      { re: /\[IMU\]/, pct: 26, label: 'Controllo del sensore di movimento…' },
      { re: /\[GNSS\]/, pct: 32, label: 'Controllo del modulo GPS…' },
      { re: /\[MODEM\] (?:Init|Power-on)/, pct: 38, label: 'Accensione del modem (circa 15 secondi)…' },
      { re: /\[MODEM\] (?:OK|Configurazione)/, pct: 50, label: 'Modem acceso, configurazione…' },
      { re: /\[RETE\] Attesa SIM READY/, pct: 56, label: 'Lettura della SIM (fino a 30 secondi)…' },
      { re: /\[RETE\] (?:SIM OK|Registrazione rete)/, pct: 64, label: 'Ricerca della rete cellulare (può richiedere un minuto)…' },
      { re: /\[RETE\] (?:Attach packet data|Attivazione PDP)/, pct: 78, label: 'Collegamento dati…' },
      { re: /\[RETE\] Recovery LTE/, pct: 64, label: 'Rete non trovata, nuovo tentativo…' },
      { re: /\[UDP\] Apertura/, pct: 86, label: 'Collegamento al server…' },
      { re: /══ MODE: FULL TRACKING/, pct: 94, label: 'Avvio completato, ultimi controlli…' },
      { re: /\[LOOP\]/, pct: 100, label: 'Controlli completati' },
    ],

    // ── riavvii inattesi durante il test (prima del nuovo banner) ──
    reboot: {
      crash: [/Guru Meditation Error/, /abort\(\) was called/, /\bBacktrace:/],
      brownout: [/Brownout detector was triggered/, /rst:0x[0-9a-f]+ \([A-Z_]*BROWN/],
      watchdog: [/Task watchdog got triggered/, /rst:0x[0-9a-f]+ \([A-Z0-9_]*WDT/],
      expected: [/\[OTA\] UPDATE OK/],
    },

    // ── righe informative ──
    notes: [
      { re: /\[WARN\] ARDUINO_USB_CDC_ON_BOOT=1/, level: 'warn', msg: 'Firmware compilato con ARDUINO_USB_CDC_ON_BOOT=1: segnalalo all\'assistenza.' },
      { re: /\[OTA\] (?:DOWNLOAD|Download|Scritti|Firmware pending verify)/, level: 'warn', msg: 'Aggiornamento del firmware in corso: non scollegare la scheda.' },
      { re: /\[BOOT\] Wake from deep sleep \(boot #(\d+)\)/, level: 'info', msg: 'La scheda si è svegliata da sola dal riposo (normale).' },
      { re: /Config:\s*DEBUG=0/, level: 'warn', msg: 'Il firmware ha i messaggi di debug spenti: alcuni guasti potrebbero non essere riconosciuti.' },
    ],

    checks: {
      gnss: {
        ok: [{ re: /\[GNSS\] Boot UART: (?!<no bytes>)\S/, detail: () => 'risponde all\'avvio' }],
        fail: [{ re: /\[GNSS\] Boot UART: <no bytes>/, msg: 'Modulo GPS non risponde' }],
      },
      modem: {
        ok: [
          { re: /\[MODEM\] OK @(\d+)/, detail: (m) => `seriale a ${m[1]} baud` },
          { re: /\[MODEM\] OK\b/ },
        ],
        fail: [
          { re: /\[MODEM\] ERRORE: nessuna risposta AT/, msg: 'Modem non risponde' },
          { re: /\[MODEM\] Init fallita/, msg: 'Modem non risponde' },
        ],
      },
      sim: {
        dependsOn: 'modem',
        ok: [{ re: /\[RETE\] SIM OK/ }],
        fail: [
          { re: /\[RETE\] ERRORE: SIM richiede PIN/, msg: 'SIM bloccata da PIN' },
          { re: /\[RETE\] ERRORE: timeout SIM READY/, msg: 'SIM assente o non letta' },
        ],
      },
      network: {
        dependsOn: 'sim',
        failAs: 'warn',
        // NXR01C v0.6.0: la registrazione può riuscire con "waitForNetwork OK"
        // senza mai stampare CEREG/CGREG
        steps: [
          { re: /\[RETE\] (CEREG|CGREG|waitForNetwork) OK/, detail: (m) => (m[1] === 'waitForNetwork' ? 'registrata' : `registrata (${m[1]})`) },
          { re: /\[RETE\] CGATT OK/, detail: () => 'registrata, dati attivi' },
        ],
        fail: [
          { re: /\[RETE\] ERRORE: timeout registrazione/, msg: 'Nessuna rete: prova vicino a una finestra o all\'aperto' },
          { re: /\[RETE\] ERRORE: timeout packet attach/, msg: 'Rete trovata ma senza dati: prova vicino a una finestra o all\'aperto' },
          { re: /\[RETE\] ERRORE: configurazione APN fallita/, msg: 'Configurazione della rete dati non riuscita' },
          { re: /\[RETE\] ERRORE: CGACT fallito/, msg: 'Attivazione della rete dati non riuscita' },
          { re: /\[RETE\] Init LTE fallita/, msg: 'Nessuna rete: prova vicino a una finestra o all\'aperto' },
        ],
        onTimeout: { status: 'warn', msg: 'Rete non trovata entro la fine del test: prova vicino a una finestra o all\'aperto' },
      },
      server: {
        dependsOn: 'network',
        failAs: 'warn',
        ok: [
          { re: /\[UDP\] OK/, detail: () => 'collegamento aperto' },
          { re: /\[HB\] OK/, detail: () => 'il server risponde' },
          { re: /\[LOOP\].*TX:(\d+).*UDP:OK/, detail: (m) => `${m[1]} posizioni inviate` },
        ],
        fail: [
          { re: /\[HB\] FAIL/, msg: 'Il server non risponde' },
          { re: /\[LOOP\].*UDP:NO/, msg: 'Collegamento al server non attivo' },
          { re: /\[NET\] NETOPEN timeout/, msg: 'Collegamento al server non riuscito' },
          { re: /\[UDP\] Errore apertura socket/, msg: 'Collegamento al server non riuscito' },
        ],
        onTimeout: { status: 'warn', msg: 'Collegamento al server non verificato entro la fine del test' },
      },
      i2c: {
        fail: [{ re: /\[I2C\] No devices found/, msg: 'Sensori interni non rilevati' }],
      },
    },
    // bus I2C: indirizzi trovati e fine della scansione
    i2cFound: /\[I2C\] Found 0x([0-9A-Fa-f]{2})/,
    i2cScanEnd: /\[BAT\]|\[IMU\]/,
  },

  NXR01B: {
    expectedFirmware: '0.4',
    // righe che riconoscono il modello quando il banner non si è visto
    signature: [/\[IMU\] Init BNO086/, /\[BAT\] OK - /, /\[I2C\] Found 0x4[AB]/, /\[BAT\] NA\b/],
    i2cRequired: [
      { any: [0x36], label: 'lettore batteria (0x36)' },
      { any: [0x4A, 0x4B], label: 'sensore di movimento (0x4A/0x4B)' },
    ],
    checks: {
      battery: {
        ok: [{ re: /\[BAT\] OK - ([\d.]+)% \/ ([\d.]+)V/, values: (m) => ({ pct: n(m[1]), volts: n(m[2]) }) }],
        fail: [{ re: /\[BAT\] NA\b/, msg: 'Lettore batteria guasto' }],
      },
      imu: {
        ok: [{ re: /\[IMU\] OK \(Accel \+ Gyro\)/, detail: () => 'BNO086 accelerometro e giroscopio' }],
        fail: [
          { re: /\[IMU\] Non trovato/, msg: 'Sensore di movimento guasto' },
          { re: /\[IMU\] Errore init/, msg: 'Sensore di movimento guasto' },
          { re: /\[IMU\] Errore report accel/, msg: 'Sensore di movimento guasto (accelerometro)' },
          { re: /\[IMU\] Errore report gyro/, msg: 'Sensore di movimento guasto (giroscopio)' },
        ],
      },
    },
  },

  NXR01C: {
    expectedFirmware: '0.6',
    signature: [/LSM6DSOX/, /MAX17260/, /\[I2C\] Found 0x6A/],
    i2cRequired: [
      { any: [0x36], label: 'lettore batteria (0x36)' },
      { any: [0x6A], label: 'sensore di movimento (0x6A)' },
    ],
    checks: {
      battery: {
        ok: [{ re: /\[BAT\] MAX17260 OK - ([\d.]+)% \/ ([\d.]+)V/, values: (m) => ({ pct: n(m[1]), volts: n(m[2]) }) }],
        fail: [
          { re: /\[BAT\] MAX17260 unavailable/, msg: 'Lettore batteria guasto' },
          { re: /\[BAT\] MAX17260 startup timeout/, msg: 'Lettore batteria guasto (non si avvia)' },
          { re: /\[BAT\] MAX17260 .*failed/, msg: 'Lettore batteria guasto' },
        ],
      },
      imu: {
        ok: [{ re: /\[IMU\] OK - WHO_AM_I=(0x[0-9A-Fa-f]{2})/, detail: (m) => `LSM6DSOX, WHO_AM_I ${m[1]}` }],
        fail: [
          { re: /\[IMU\] Init failed/, msg: 'Sensore di movimento guasto' },
          { re: /\[IMU\] Configuration failed/, msg: 'Sensore di movimento guasto' },
          { re: /\[IMU\] WHO_AM_I mismatch/, msg: 'Sensore di movimento guasto (identificativo errato)' },
        ],
        warn: [{ re: /\[IMU\] Sample read failed/, msg: 'Letture del sensore di movimento non riuscite' }],
      },
    },
  },
};

// Controlli mostrati al cliente, nell'ordine dell'avvio.
export const CHECKS = [
  { id: 'comm', label: 'Comunicazione con la scheda' },
  { id: 'i2c', label: 'Sensori interni' },
  { id: 'battery', label: 'Lettore batteria' },
  { id: 'charge', label: 'Carica della batteria' },
  { id: 'imu', label: 'Sensore di movimento' },
  { id: 'gnss', label: 'Modulo GPS' },
  { id: 'modem', label: 'Modem 4G' },
  { id: 'sim', label: 'SIM' },
  { id: 'network', label: 'Rete cellulare' },
  { id: 'signal', label: 'Segnale' },
  { id: 'server', label: 'Collegamento al server' },
  { id: 'led', label: 'LED' },
];

export { fmt as fmtNumber };
